import * as Yup from 'yup';
import { SchemaLike } from 'yup/lib/types';
import { getRequiredText } from '../helpers/transformer';
import { RegularExpressions } from '../constants/regular-expressions';
import { LabelSettingProps } from '../models/epi-model';
import { YesNoIndicators } from '../models/api-model';

const getFieldNameWithPrefix = (addressType: string, fieldName: string) => {
	const prefix = addressType.length ? `${addressType} ` : '';

	return `${prefix}${fieldName}`;
};

export const address_1_validation = (labelSetting: LabelSettingProps, addressType: string) => {
	const fieldNameWithPrefix = getFieldNameWithPrefix(addressType, labelSetting.addressLabel1);

	return Yup.string().nullable()
		.matches(/^[a-zA-ZÀ-ÖØ-öø-ÿ0-9#/\-' ]*$/,
			labelSetting.specialCharactersError || 'No special characters are allowed')
		.matches(/^(?!.*\s{2,})(?!\s)(?!.*\s$).*$/,
			'No leading, trailing, or consecutive spaces allowed')
		.test(
			'contains-letter',
			labelSetting.mustContainAlphabeticalErrorMessage || 'Address must contain alphabetic characters',
			value => !value || /[a-zA-ZÀ-ÖØ-öø-ÿ]/.test(value),
		)
		.max(40, labelSetting.maxCharactersError?.replace('{max}', '40') || 'Maximum 40 characters')
		.required(getRequiredText(labelSetting.requiredFormat, fieldNameWithPrefix))
		.test(
			'empty-or-2-characters-check',
			labelSetting.min2CharacterErrorMessage,
			value => !value || value?.replace(/\s+/g, '')?.length >= 2,
		);
};

const validation_nonepiscopal = (thenSchema: SchemaLike, otherwiseSchema?: SchemaLike) => {
	return Yup.string().ensure().when(['nonEpiscopal'],
		{
			is: YesNoIndicators.Yes,
			then: thenSchema,
			otherwise: otherwiseSchema,
		});
};

export const address_2_validation = (labelSetting: LabelSettingProps, address1FieldName?: string, address3FieldName?: string, addressType?: string) => {
	const fieldNameWithPrefix = addressType && addressType.length 
		? `${addressType} ${labelSetting.addressLabel2}` 
		: labelSetting.addressLabel2;
	const requiredMessage = getRequiredText(labelSetting.requiredFormat, fieldNameWithPrefix);
	
	let validation = Yup.string().nullable()
		.matches(/^[a-zA-ZÀ-ÖØ-öø-ÿ0-9#/\-' ]*$/,
			labelSetting.specialCharactersError || 'No special characters are allowed')
		.matches(/^(?!.*\s{2,})(?!\s)(?!.*\s$).*$/,
			'No leading, trailing, or consecutive spaces allowed')
		.test(
			'contains-letter',
			labelSetting.mustContainAlphabeticalErrorMessage || 'Address must contain alphabetic characters',
			value => !value || /[a-zA-ZÀ-ÖØ-öø-ÿ]/.test(value),
		)
		.max(40, labelSetting.maxCharactersError?.replace('{max}', '40') || 'Maximum 40 characters')
		.test(
			'empty-or-2-characters-check',
			labelSetting.min2CharacterErrorMessage,
			value => !value || value?.replace(/\s+/g, '')?.length >= 2,
		);
	
	// Add conditional validation if address1 and address3 field names are provided
	if (address1FieldName && address3FieldName) {
		validation = validation.test(
			'address2-required-when-address3-filled',
			requiredMessage,
			function (value) {
				const address1 = this.parent[address1FieldName];
				const address3 = this.parent[address3FieldName];
				
				const hasAddress1 = address1 && typeof address1 === 'string' && address1.trim().length > 0;
				const hasAddress3 = address3 && typeof address3 === 'string' && address3.trim().length > 0;
				
				// If both address1 and address3 are filled, address2 is required
				if (hasAddress1 && hasAddress3) {
					const hasAddress2 = value && typeof value === 'string' && value.trim().length > 0;
					return hasAddress2;
				}
				
				return true;
			},
		);
	}
	
	return validation;
};
export const address_3_validation = (labelSetting: LabelSettingProps) => {

	return Yup.string().nullable()
		.matches(/^[a-zA-ZÀ-ÖØ-öø-ÿ0-9#/\-' ]*$/,
			labelSetting.specialCharactersError || 'No special characters are allowed')
		.matches(/^(?!.*\s{2,})(?!\s)(?!.*\s$).*$/,
			'No leading, trailing, or consecutive spaces allowed')
		.test(
			'contains-letter',
			labelSetting.mustContainAlphabeticalErrorMessage || 'Address must contain alphabetic characters',
			value => !value || /[a-zA-ZÀ-ÖØ-öø-ÿ]/.test(value),
		)
		.max(40, labelSetting.maxCharactersError?.replace('{max}', '40') || 'Maximum 40 characters')
		.test(
			'empty-or-2-characters-check',
			labelSetting.min2CharacterErrorMessage,
			value => !value || value?.replace(/\s+/g, '')?.length >= 2,
		);
};

export const country_validation = (labelSetting: LabelSettingProps, addressType: string) => {
	const fieldNameWithPrefix = getFieldNameWithPrefix(addressType, labelSetting.countryLabel);

	return Yup.string().required(getRequiredText(labelSetting.requiredFormat, `${fieldNameWithPrefix}`));
};

export const city_validation = (labelSetting: LabelSettingProps, addressType: string) => {
	const fieldNameWithPrefix = getFieldNameWithPrefix(addressType, labelSetting.cityLabel);

	return Yup.string().nullable()
		.required(getRequiredText(labelSetting.requiredFormat, `${fieldNameWithPrefix}`))
		.matches(/^[a-zA-ZÀ-ÖØ-öø-ÿ \-']*$/,
			labelSetting.specialCharactersError || 'No special characters are allowed')
		.matches(/^(?!.*\s{2,})(?!\s)(?!.*\s$).*$/,
			'No leading, trailing, or consecutive spaces allowed')
		.test(
			'contains-letter',
			labelSetting.mustContainAlphabeticalErrorMessage || 'City must contain alphabetic characters',
			value => !value || /[a-zA-ZÀ-ÖØ-öø-ÿ]/.test(value),
		)
		.max(40, labelSetting.maxCharactersError?.replace('{max}', '40') || 'Maximum 40 characters')
		.test(
			'empty-or-2-characters-check',
			labelSetting.min2CharacterErrorMessage,
			value => !value || value?.replace(/\s+/g, '')?.length >= 2,
		);
};

export const state_validation = (labelSetting: LabelSettingProps, countryFieldName: string, addressType: string) => {
	const stateLabelWithPrefix = getFieldNameWithPrefix(addressType, labelSetting.stateLabel);
	const provinceLabelWithPrefix = getFieldNameWithPrefix(addressType, labelSetting.provinceLabel);


	return Yup.string().nullable().ensure().when(countryFieldName,
		{
			is: 'US',
			then: Yup.string().nullable().required(getRequiredText(labelSetting.requiredFormat, `${stateLabelWithPrefix}`)),
		}).ensure().when(countryFieldName,
			{
				is: 'CA',
				then: Yup.string().nullable().required(getRequiredText(labelSetting.requiredFormat, `${provinceLabelWithPrefix}`)),
			}).ensure().when(countryFieldName,
				{
					is: 'MX',
					then: Yup.string().nullable().required(getRequiredText(labelSetting.requiredFormat, `${provinceLabelWithPrefix}`)),
				});
};

export const postal_code_validation = (labelSetting: LabelSettingProps, countryFieldName: string, addressType: string) => {
	const fieldNameWithPrefix = getFieldNameWithPrefix(addressType, labelSetting.postalLabel);

	return Yup.string().nullable().ensure().when(
		countryFieldName,
		{
			is: 'US',
			then: Yup.string().nullable()
				.matches(RegularExpressions.USPostalCode, `${labelSetting.invalidError || 'Invalid'} ${fieldNameWithPrefix}`),
		}).ensure().when(countryFieldName,
			{
				is: 'CA',
				then: Yup.string().nullable()
					.matches(RegularExpressions.CAPostalCode, `${labelSetting.invalidError || 'Invalid'} ${fieldNameWithPrefix}`),
			}).ensure().when(countryFieldName,
				{
					is: 'MX',
					then: Yup.string().nullable()
						.matches(RegularExpressions.MXPostalCode, `${labelSetting.invalidError || 'Invalid'} ${fieldNameWithPrefix}`),
				});
};

export const PersonInfoMailingAddressValidationSchema = (labelSetting: LabelSettingProps, ignore?: boolean) => {
	if (ignore) return {};

	const addressType = '';

	return {
		mailing_address_1: address_3_validation(labelSetting),
		mailing_address_2: address_2_validation(labelSetting),
		mailing_address_3: address_3_validation(labelSetting),
		mailing_city: Yup.string().nullable()
			.matches(/^[a-zA-ZÀ-ÖØ-öø-ÿ \-']*$/,
				labelSetting.specialCharactersError || 'No special characters are allowed')
			.matches(/^(?!.*\s{2,})(?!\s)(?!.*\s$).*$/,
				'No leading, trailing, or consecutive spaces allowed')
			.test(
				'contains-letter',
				labelSetting.mustContainAlphabeticalErrorMessage || 'City must contain alphabetic characters',
				value => !value || /[a-zA-ZÀ-ÖØ-öø-ÿ]/.test(value),
			)
			.max(40, labelSetting.maxCharactersError?.replace('{max}', '40') || 'Maximum 40 characters')
			.test(
				'empty-or-2-characters-check',
				labelSetting.min2CharacterErrorMessage,
				value => !value || value?.replace(/\s+/g, '')?.length >= 2,
			),
		mailing_state: state_validation(labelSetting, 'mailing_country', addressType),
		mailing_postal_code: postal_code_validation(labelSetting, 'mailing_country', addressType),
	};
};

export const ClergyBioAddressValidationSchema = (labelSetting: LabelSettingProps) => {

	const addressType = '';

	return {
		address1: address_1_validation(labelSetting, addressType),
		address2: address_2_validation(labelSetting),
		address3: address_3_validation(labelSetting),
		city: city_validation(labelSetting, addressType),
		state: state_validation(labelSetting, 'country', ''),
		postalCode: postal_code_validation(labelSetting, 'country', ''),
	};
};

export const InstitutionMailingAddressValidationSchema = (labelSetting: LabelSettingProps, ignore?: boolean) => {
	if (ignore) return {};

	const addressType = 'Mailing';

	return {
		billing_address_1: address_1_validation(labelSetting, addressType),
		billing_address_2: address_2_validation(labelSetting),
		billing_address_3: address_3_validation(labelSetting),
		billing_country: country_validation(labelSetting, addressType),
		billing_city: city_validation(labelSetting, addressType),
		billing_state: state_validation(labelSetting, 'billing_country', addressType),
		billing_postal_code: postal_code_validation(labelSetting, 'billing_country', addressType),
	};
};

export const PersonInfoNonEpiscopalMailingAddressValidationSchema = (labelSetting: LabelSettingProps, ignore?: boolean) => {
	if (ignore) return {};

	const addressType = 'Mailing';

	return {
		mailing_address_1: validation_nonepiscopal(address_1_validation(labelSetting, addressType), address_3_validation(labelSetting)),
		mailing_address_2: address_2_validation(labelSetting),
		mailing_address_3: address_3_validation(labelSetting),
		mailing_country: validation_nonepiscopal(country_validation(labelSetting, addressType)),
		mailing_city: validation_nonepiscopal(city_validation(labelSetting, addressType)),
		mailing_state: state_validation(labelSetting, 'mailing_country', addressType),
		mailing_postal_code: postal_code_validation(labelSetting, 'mailing_country', addressType),
	};
};

export const InstitutionPhysicalAddressValidationSchema = (labelSetting: LabelSettingProps, ignore?: boolean) => {
	if (ignore) return {};

	const addressType = 'Physical';

	return {
		physical_address_1: address_1_validation(labelSetting, addressType),
		physical_address_2: address_2_validation(labelSetting),
		physical_address_3: address_3_validation(labelSetting),
		physical_country: country_validation(labelSetting, addressType),
		physical_city: city_validation(labelSetting, addressType),
		physical_state: state_validation(labelSetting, 'physical_country', addressType),
		physical_postal_code: postal_code_validation(labelSetting, 'physical_country', addressType),
	};
};

export const NewHireMailingAddressValidationSchema = (labelSetting: LabelSettingProps, ignore?: boolean) => {
	if (ignore) return {};

	const addressType = 'Mailing';

	return {
		mailing_address_1: address_1_validation(labelSetting, addressType),
		mailing_address_2: address_2_validation(labelSetting, 'mailing_address_1', 'mailing_address_3', addressType),
		mailing_address_3: address_3_validation(labelSetting),
		mailing_country: country_validation(labelSetting, addressType),
		mailing_city: city_validation(labelSetting, addressType),
		mailing_state: state_validation(labelSetting, 'mailing_country', addressType),
		mailing_postal_code: postal_code_validation(labelSetting, 'mailing_country', addressType),
	};
};

export const InstitutionAddRequestMailingAddressValidationSchema = (labelSetting: LabelSettingProps, ignore?: boolean) => {
	if (ignore) return {};

	const addressType = 'Mailing';

	return {
		mail_address1: address_1_validation(labelSetting, addressType),
		mail_address2: address_2_validation(labelSetting),
		mail_address3: address_3_validation(labelSetting),
		mail_country: country_validation(labelSetting, addressType),
		mail_city: city_validation(labelSetting, addressType),
		mail_state: state_validation(labelSetting, 'mail_country', addressType),
		mail_postal_code: postal_code_validation(labelSetting, 'mail_country', addressType),
	};
};

export const InstitutionAddRequestPhysicalAddressValidationSchema = (labelSetting: LabelSettingProps, ignore?: boolean) => {
	if (ignore) return {};

	const addressType = 'Physical';

	return {
		street_address1: address_1_validation(labelSetting, addressType),
		street_address2: address_2_validation(labelSetting),
		street_address3: address_3_validation(labelSetting),
		street_country: country_validation(labelSetting, addressType),
		street_city: city_validation(labelSetting, addressType),
		street_state: state_validation(labelSetting, 'street_country', addressType),
		street_postal_code: postal_code_validation(labelSetting, 'street_country', addressType),
	};
};